import { useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { CurrencyInr, CalendarCheck, Exam } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useApi, inr, fmtDate, monthISO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, Loader, Empty, Badge, ExportButtons, ReportSection, DataTable } from "../components/kit";
import { UdiseReport } from "./Udise";

const tabCls = "rounded-full px-5 py-2 text-sm text-zinc-400 transition-colors duration-200 hover:text-white data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_24px_-8px_rgba(59,130,246,0.7)]";
const tip = { contentStyle: { background: "#0c0c16", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, color: "#fff" }, cursor: { fill: "rgba(255,255,255,0.03)" } };

const Kpi = ({ label, value, tone = "text-white", testid }) => (
  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4">
    <div className="eyebrow !text-[10px]">{label}</div>
    <div className={`mt-1.5 truncate font-display text-xl sm:text-2xl font-semibold tabular-nums ${tone}`} data-testid={testid}>{value}</div>
  </div>
);

function PendingFees({ t }) {
  const { data: classes } = useApi("/classes");
  const [cls, setCls] = useState("");
  const { data, loading } = useApi(`/reports/pending-fees?class_id=${cls}`);
  const rows = data?.rows || [];
  return (
    <ReportSection icon={CurrencyInr} tone="text-orange-300" title={t("Pending fees")} subtitle="Outstanding invoices by student, with parent contact." testid="report-fees-section"
      actions={<ExportButtons entity="invoices" />}>
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <select className="field" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="report-fee-class">
          <option value="">{t("All classes")}</option>
          {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
        </select>
        <Kpi label="Outstanding" value={<span data-testid="report-pending-total">{inr(data?.total)}</span>} tone="text-orange-300" />
        <Kpi label="Open invoices" value={rows.length} testid="report-pending-count" />
        <Kpi label="Overdue" value={rows.filter((r) => r.overdue).length} tone="text-rose-300" testid="report-overdue-count" />
      </div>
      {loading ? <Loader /> : !rows.length ? <Empty title="No pending fees" /> : (
        <DataTable testid="pending-fees-table" minWidth={900} head={["Student", t("Class"), t("Title"), "Balance", t("Due date"), t("Parent"), t("Phone"), t("Status")]}
          rows={rows.map((r) => [r.student_name, r.class_name, r.title, <span className="text-orange-300">{inr(r.balance)}</span>, fmtDate(r.due_date), r.parent_name, r.parent_phone,
            r.overdue ? <Badge status="A">Overdue</Badge> : <Badge status={r.status} />])} />
      )}
    </ReportSection>
  );
}

function AttendanceReport({ t, isAdmin }) {
  const [month, setMonth] = useState(monthISO());
  const { data, loading } = useApi(`/reports/attendance?month=${month}`);
  const rows = data?.rows || [];
  const marked = rows.filter((r) => r.percent != null);
  const avg = marked.length ? Math.round(marked.reduce((a, r) => a + r.percent, 0) / marked.length) : null;
  return (
    <ReportSection icon={CalendarCheck} title={t("Attendance")} subtitle="Monthly attendance percentage per class." testid="report-attendance-section"
      actions={isAdmin && <ExportButtons entity="attendance" />}>
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <input type="month" className="field" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-att-month" />
        <Kpi label="Average attendance" value={avg != null ? `${avg}%` : "—"} tone="text-emerald-300" testid="report-att-avg" />
        <Kpi label="Classes" value={rows.length} />
        <Kpi label="Below 75%" value={marked.filter((r) => r.percent < 75).length} tone="text-rose-300" />
      </div>
      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="h-[300px] lg:col-span-3">
            <ResponsiveContainer>
              <BarChart data={rows.map((r) => ({ ...r, label: `${r.class_name}`, percent: r.percent || 0 }))}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="label" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} interval={0} angle={-30} textAnchor="end" height={56} />
                <YAxis domain={[0, 100]} stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={34} />
                <Tooltip {...tip} />
                <Bar dataKey="percent" name="Attendance %" fill="#60A5FA" radius={[8, 8, 3, 3]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2">
            <DataTable testid="attendance-report-table" head={[t("Class"), "Days marked", "%"]}
              rows={rows.map((r) => [r.class_name, r.days, <span className={r.percent >= 75 ? "text-emerald-300" : "text-rose-300"}>{r.percent ?? "—"}%</span>])} />
          </div>
        </div>
      )}
    </ReportSection>
  );
}

function AcademicReport({ t }) {
  const { data: exams } = useApi("/exams");
  const [eid, setEid] = useState("");
  const id = eid || (exams?.find((e) => e.marks_entered > 0) || exams?.[0])?.id;
  const { data, loading } = useApi(id ? `/reports/academic?exam_id=${id}` : null);
  return (
    <ReportSection icon={Exam} tone="text-orange-300" title={t("Academic performance")} subtitle="Subject averages and student ranking for an exam." testid="report-academic-section">
      <select className="field sm:!w-80 mb-6" value={id || ""} onChange={(e) => setEid(e.target.value)} data-testid="report-exam-select">
        {exams?.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.class_name}</option>)}
      </select>
      {!id ? <Empty /> : loading || !data ? <Loader /> : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Class average" value={data.class_average != null ? `${data.class_average}%` : "—"} tone="text-blue-300" />
            <Kpi label="Students graded" value={data.total} />
            <Kpi label="Passed" value={data.pass_count} tone="text-emerald-300" />
            <Kpi label="Top scorer" value={data.rows[0]?.name || "—"} />
          </div>
          <div className="h-[240px]">
            <ResponsiveContainer>
              <BarChart data={data.subjects}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="subject" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={34} />
                <Tooltip {...tip} />
                <Bar dataKey="average" fill="#F97316" radius={[8, 8, 3, 3]} name="Average %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {!data.rows.length ? <Empty hint="No marks entered for this exam yet." /> : (
            <DataTable testid="academic-report-table" minWidth={720} head={[t("Rank"), t("Name"), ...data.exam.subjects.map((s) => s.name), t("Total"), "%", t("Grade")]}
              rows={data.rows.map((r, i) => [i + 1, r.name, ...r.subjects.map((s) => s.marks ?? "—"), `${r.total}/${r.max_total}`, <span className="text-blue-300">{r.percent}%</span>, r.grade])} />
          )}
        </div>
      )}
    </ReportSection>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user.role === "admin";
  return (
    <div>
      <PageHeader eyebrow="Insights" title={isAdmin ? t("Reports") : t("Class-wise report")} subtitle={isAdmin ? "Pending fees, attendance, academic performance and UDISE+ returns — each with Excel and CSV downloads." : "Attendance and academic performance for your classes."} />
      <Tabs defaultValue={isAdmin ? "fees" : "attendance"}>
        <TabsList className="mb-8 h-auto flex-wrap gap-1 rounded-full border border-white/10 bg-white/5 p-1.5">
          {isAdmin && <TabsTrigger value="fees" className={tabCls} data-testid="tab-report-fees">{t("Pending fees")}</TabsTrigger>}
          <TabsTrigger value="attendance" className={tabCls} data-testid="tab-report-attendance">{t("Attendance")}</TabsTrigger>
          <TabsTrigger value="academic" className={tabCls} data-testid="tab-report-academic">{t("Academic performance")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="udise" className={tabCls} data-testid="tab-report-udise">UDISE+</TabsTrigger>}
        </TabsList>
        {isAdmin && <TabsContent value="fees"><PendingFees t={t} /></TabsContent>}
        <TabsContent value="attendance"><AttendanceReport t={t} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="academic"><AcademicReport t={t} /></TabsContent>
        {isAdmin && <TabsContent value="udise"><UdiseReport /></TabsContent>}
      </Tabs>
    </div>
  );
}
